/**
 * HTTP-клиент.
 *
 * Единая точка обращения к API: разбирает контрактный формат ошибки,
 * передаёт cookie сессии и подставляет CSRF-заголовок для запросов,
 * изменяющих состояние.
 */

import { CSRF_HEADER, type ApiErrorBody, type ApiErrorCode } from '@project-os/contracts';

/** Все запросы идут через прокси dev-сервера, поэтому источник один. */
const API_PREFIX = '/api';

/**
 * Адрес изображения, отдаваемого API.
 *
 * Сервер хранит путь без префикса — он не знает и не должен знать, под
 * каким адресом его видит браузер: префикс задаёт прокси. Приписывается
 * он здесь, в одном месте, а не в каждом компоненте, который рисует
 * картинку.
 */
export function mediaUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  if (/^https?:/.test(path)) return path;
  return `${API_PREFIX}${path}`;
}

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly fields?: Record<string, string>;
  readonly requestId?: string;
  /** Подсказка, что предпринять. Показывается под сообщением об ошибке. */
  readonly hint?: string;

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    fields?: Record<string, string>,
    requestId?: string,
    hint?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.requestId = requestId;
    this.hint = hint;
  }

  /** Запрос не ушёл: браузер не смог установить соединение. */
  static network(): ApiError {
    return new ApiError(
      0,
      'SERVICE_UNAVAILABLE',
      'Нет связи с сервером.',
      undefined,
      undefined,
      'Проверьте подключение к сети и что приложение открыто по адресу http://localhost:5173.',
    );
  }

  /**
   * Ответ пришёл, но не от приложения: сервер разработки не смог достучаться
   * до API либо API вернул страницу вместо контракта.
   */
  static unreachable(status: number): ApiError {
    return new ApiError(
      status,
      'SERVICE_UNAVAILABLE',
      'API не отвечает.',
      undefined,
      undefined,
      'Запустите backend командой «npm run dev:api». Если он уже запущен — проверьте, ' +
        'что подняты PostgreSQL и MinIO: «npm run infra:up».',
    );
  }
}

/** Парный к сессии токен CSRF: сервер кладёт его в cookie, доступную скрипту. */
function readCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)pos_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Загрузка файла.
 *
 * Отдельная функция, а не флаг у apiRequest: у multipart другое тело и
 * другой заголовок — Content-Type ставит сам браузер, вместе с boundary.
 * Задать его вручную значит сломать разбор на сервере.
 */
export async function apiUpload<T>(
  path: string,
  file: File,
  options: {
    field?: string;
    signal?: AbortSignal;
    /** Обычные поля формы рядом с файлом: у multipart они допустимы. */
    fields?: Record<string, string | undefined>;
  } = {},
): Promise<T> {
  const form = new FormData();
  form.append(options.field ?? 'file', file, file.name);
  for (const [key, value] of Object.entries(options.fields ?? {})) {
    if (value !== undefined) form.append(key, value);
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  const csrf = readCsrfToken();
  if (csrf) headers[CSRF_HEADER] = csrf;

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: form,
      signal: options.signal ?? null,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw ApiError.network();
  }

  return readResponse<T>(response);
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (method !== 'GET') {
    const csrf = readCsrfToken();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method,
      headers,
      // Сессия хранится в HttpOnly cookie.
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal ?? null,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw ApiError.network();
  }

  return readResponse<T>(response);
}

/** Разбор ответа и контрактного конверта ошибки. */
async function readResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;

    // Ответ без контрактного конверта означает, что до приложения запрос не
    // дошёл: обычно это остановленный backend или ошибка прокси разработки.
    // Такому случаю нужен не «что-то пошло не так», а понятная причина.
    if (!body?.error) {
      throw ApiError.unreachable(response.status);
    }

    throw new ApiError(
      response.status,
      body.error.code,
      body.error.message,
      body.error.fields,
      body?.error?.requestId,
    );
  }

  return payload as T;
}
